import { __decorate } from "tslib";
import React, { cloneElement, Component, isValidElement } from 'react';
import { observer } from 'mobx-react';
import { action, isArrayLike, observable, runInAction, toJS } from 'mobx';
import uniq from 'lodash/uniq';
import pull from 'lodash/pull';
import noop from 'lodash/noop';
import map from 'lodash/map';
import isObject from 'lodash/isObject';
import isEnumEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import isFunction from 'lodash/isFunction';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import omit from 'lodash/omit';
import defer from 'lodash/defer';
import difference from 'lodash/difference';
import classNames from 'classnames';
import ConfigContext from 'choerodon-ui/lib/config-provider/ConfigContext';
import { getProPrefixCls as getProPrefixClsDefault } from 'choerodon-ui/lib/configure/utils';
import { pxToRem } from 'choerodon-ui/lib/_util/UnitConvertor';
import Icon from 'choerodon-ui/lib/icon';
import { Action } from 'choerodon-ui/lib/trigger/enum';
import Popover from 'choerodon-ui/lib/popover';
import Tag from 'choerodon-ui/lib/tag';
import DataSet from '../../data-set/DataSet';
import { DataSetEvents, DataSetSelection, FieldIgnore, FieldType, RecordStatus } from '../../data-set/enum';
import Button from '../../button';
import Dropdown from '../../dropdown';
import TextField from '../../text-field';
import { ValueChangeAction } from '../../text-field/enum';
import Tooltip from '../../tooltip';
import { WaitType, Tooltip as OptionTooltip } from '../../core/enum';
import { $l } from '../../locale-context';
import autobind from '../../_util/autobind';
import isEmpty from '../../_util/isEmpty';
import FieldList from './FieldList';
import TableButtons from './TableButtons';
import ColumnFilter from './ColumnFilter';
import QuickFilterMenu from './quick-filter/QuickFilterMenu';
import QuickFilterMenuContext from './quick-filter/QuickFilterMenuContext';
import { ConditionDataSet, QuickFilterDataSet, NewFilterDataSet, AdvancedFieldSet } from './quick-filter/QuickFilterDataSet';
import { hide, show } from '../../tooltip/singleton';
import { ShowHelp } from '../../field/enum';
import { renderValidationMessage as utilRenderValidationMessage } from '../../field/utils';
import Select from '../../select';
import Tree from '../../tree';
import { ButtonColor, FuncType } from '../../button/enum';
import { OPERATOR, OPERATOR_TYPE } from './quick-filter/Operator';
import { getEditorByField } from '../utils';
/**
 * 当前数据是否有值并需要选中
 * @param data
 */
export function isSelect(data) {
    if (isObject(data[1])) {
        return !isEnumEmpty(data[1]);
    }
    return data[0] !== '__dirty' && !isEmpty(data[1]);
}
export function isEqualDynamicProps(originalValue, newValue, dataSet, record, name) {
    if (isEqual(newValue, originalValue)) {
        return true;
    }
    if (isObject(newValue) && isObject(originalValue) && Object.keys(newValue).length) {
        const combineKeys = uniq(Object.keys(newValue).concat(Object.keys(originalValue)));
        return combineKeys.every(key => {
            const value = toJS(newValue[key]);
            const oldValue = toJS(originalValue[key]);
            if (oldValue === value) {
                return true;
            }
            if (isEmpty(oldValue) && isEmpty(value)) {
                return true;
            }
            if (name && name.includes('.')) {
                return isEmpty(oldValue) && isEmpty(record.get(name));
            }
            if (isNumber(oldValue) || isNumber(value)) {
                const oEp = isNumber(oldValue) ? isEmpty(oldValue) : isEnumEmpty(oldValue);
                const nEp = isNumber(value) ? isEmpty(value) : isEnumEmpty(value);
                if (oEp && nEp) {
                    return true;
                }
                return String(oldValue) === String(value);
            }
            const field = dataSet.getField(key);
            if (field && field.get('range', record)) {
                const rangeValue = value ? isArray(value) ? value.join('') : Object.values(value).join('') : '';
                const rangeOldValue = oldValue ? isArray(oldValue) ? oldValue.join('') : Object.values(oldValue).join('') : '';
                return rangeValue === rangeOldValue;
            }
            if (field && field.get('lovCode') && oldValue && value) {
                const valueField = dataSet.getField(key).get('valueField', record);
                const textField = dataSet.getField(key).get('textField', record);
                return value[valueField] === oldValue[valueField] && value[textField] === oldValue[textField];
            }
            return isEqual(oldValue, value);
        });
    }
    return isEqual(newValue, originalValue);
}
/**
 * 提交副本数据
 * @param data
 */
export function omitData(data) {
    return omit(data, 'creationDate', 'createdBy', 'lastUpdateDate', 'lastUpdatedBy', 'objectVersionNumber', '_token', 'searchId', 'searchConditionId', 'searchQueryId', 'searchOrderId');
}
/**
 * obj 值使用 JSON 保存、获取赋值转化
 * @param value
 */
export function parseValue(value) {
    try {
        const res = JSON.parse(value);
        if (typeof res === 'object') {
            return res;
        }
        return value;
    }
    catch (e) {
        return value;
    }
}
export function stringifyValue(value) {
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return value;
}
/**
 * 处理查询过滤条件
 * @params
 */
export function processFilterParam(dataSet) {
    const newFilterDataSet = dataSet.getState(NEWFILTERDATASET);
    // 处理公式参数
    let searchExp = '';
    const expType = dataSet.getState(EXPTYPE);
    if (expType !== 'customize') {
        const joinStr = (expType === 'all' || expType === undefined) ? 'AND' : 'OR';
        for (let i = 1; i <= newFilterDataSet.length; i++) {
            if (i === newFilterDataSet.length) {
                searchExp += i;
            }
            else {
                searchExp += i + joinStr;
            }
        }
    }
    else {
        searchExp = dataSet.getState(SEARCHEXP).toLocaleUpperCase().replace(/\s+/g, '');
    }
    dataSet.setQueryParameter('search.exp', searchExp);
    // 处理字段参数
    const searchFld = [];
    newFilterDataSet.map((record, index) => {
        const dataObj = record.toData();
        const name = dataObj[AdvancedFieldSet.fieldName];
        const comparator = dataObj[AdvancedFieldSet.comparator];
        const tableName = newFilterDataSet.getField(name).get('help', record);
        let value = '';
        if (isObject(dataObj[name])) {
            if (!isArray(dataObj[name])) {
                value = dataObj[name][name];
            }
            else {
                value = dataObj[name].map(arr => isObject(arr) ? arr[name] : arr);
            }
        }
        else {
            value = dataObj[name];
        }
        const paramFieldName = tableName ? `${tableName}.${name}` : name;
        let searchValue = [];
        if ([OPERATOR.IS_NULL.value, OPERATOR.IS_NOT_NULL.value].includes(comparator) || value) {
            const enCodeValue = isNumber(value) ? value : encodeURIComponent(value);
            searchValue = [index + 1, paramFieldName, dataObj[AdvancedFieldSet.comparator], enCodeValue].filter(Boolean);
        }
        if (searchValue && searchValue.length) {
            searchFld.push(searchValue.join('@'));
        }
        return null;
    });
    const searchFilParam = searchFld.join(',');
    dataSet.setQueryParameter('search.fld', searchFilParam);
}
export const CONDITIONSTATUS = '__CONDITIONSTATUS__';
export const SELECTFIELDS = '__SELECTFIELDS__';
export const MENUDATASET = '__MENUDATASET__';
export const CONDITIONDATASET = '__CONDITIONDATASET__';
export const OPTIONDATASET = '__OPTIONDATASET__';
export const FILTERMENUDATASET = '__FILTERMENUDATASET__';
export const MENURESULT = '__MENURESULT__';
export const SEARCHTEXT = '__SEARCHTEXT__';
export const SELECTCHANGE = '__SELECTCHANGE__';
export const EXPTYPE = '__EXPTYPE__';
export const SEARCHEXP = '__SEARCHEXP__';
export const NEWFILTERDATASET = '__NEWFILTERDATASET__';
export const ORIGINALVALUEOBJ = '__ORIGINALVALUEOBJ__';
let TableDynamicFilterBar = class TableDynamicFilterBar extends Component {
    constructor(props, context) {
        super(props, context);
        this.refDropdown = null;
        this.refSingleWrapper = null;
        this.refEditors = new Map();
        this.refFilterItems = new Map();
        this.originalConditionFields = [];
        this.handleClickOut = (e) => {
            if (this.refDropdown && !this.refDropdown.contains(e.target)) {
                this.fieldSelectHidden = true;
            }
        };
        this.setOriginalConditionFields = (code) => {
            const { queryDataSet, dataSet } = this.props;
            if (!code) {
                if (queryDataSet) {
                    this.initConditionFields({ dataSet: queryDataSet, record: queryDataSet.get(0) });
                }
            }
            else {
                this.originalConditionFields = Array.isArray(code) ? code : [code];
            }
            dataSet.setState(SELECTFIELDS, [...this.originalConditionFields]);
        };
        /**
         * 勾选
         * @param code
         * @param record
         */
        this.handleSelect = (code, record) => {
            const { dataSet } = this.props;
            const codes = Array.isArray(code) ? code : [code];
            const selectFields = dataSet.getState(SELECTFIELDS) || [];
            dataSet.setState(SELECTFIELDS, uniq([...selectFields, ...codes]));
            const shouldUpdate = dataSet.getState(SELECTFIELDS).length !== this.originalConditionFields.length
                || !!difference(toJS(dataSet.getState(SELECTFIELDS)), toJS(this.originalConditionFields)).length;
            const isDirty = record ? record.dirty : false;
            this.setConditionStatus(shouldUpdate || isDirty ? RecordStatus.update : RecordStatus.sync);
            dataSet.setState(SELECTCHANGE, shouldUpdate);
        };
        /**
         * 取消勾选
         * @param code
         */
        this.handleUnSelect = (code) => {
            const { queryDataSet, dataSet } = this.props;
            const codes = Array.isArray(code) ? code : [code];
            if (queryDataSet) {
                const { current } = queryDataSet;
                if (current) {
                    codes.forEach((name) => current.set(name, undefined));
                }
            }
            const selectFields = dataSet.getState(SELECTFIELDS) || [];
            dataSet.setState(SELECTFIELDS, pull([...selectFields], ...codes));
            const shouldUpdate = dataSet.getState(SELECTFIELDS).length !== this.originalConditionFields.length
                || !!difference(toJS(dataSet.getState(SELECTFIELDS)), toJS(this.originalConditionFields)).length;
            this.setConditionStatus(shouldUpdate ? RecordStatus.update : RecordStatus.sync);
            dataSet.setState(SELECTCHANGE, shouldUpdate);
        };
        runInAction(() => {
            this.fieldSelectHidden = true;
            this.expand = true;
        });
    }
    static get contextType() {
        return ConfigContext;
    }
    get prefixCls() {
        const { prefixCls } = this.props;
        const { getProPrefixCls = getProPrefixClsDefault } = this.context;
        return getProPrefixCls('table', prefixCls);
    }
    get queryFields() {
        const { queryFields, queryDataSet, dataSet } = this.props;
        const menuDataSet = dataSet.getState(MENUDATASET);
        const isTenant = menuDataSet && menuDataSet.current && menuDataSet.current.get('isTenant');
        return queryFields.filter(component => {
            if (component.props.hidden) {
                return !component.props.hidden;
            }
            if (isTenant && queryDataSet && queryDataSet.getField(component.props.name)) {
                return queryDataSet.getField(component.props.name).get('fieldVisible') !== 0;
            }
            return !component.props.hidden;
        });
    }
    componentDidMount() {
        const { fuzzyQueryOnly, queryDataSet, dataSet } = this.props;
        if (!fuzzyQueryOnly) {
            this.processDataSetListener(true);
            document.addEventListener('click', this.handleClickOut);
            if (this.isSingleLineOpt() && this.refSingleWrapper) {
                const { height } = this.refSingleWrapper.getBoundingClientRect();
                const { height: childHeight } = this.refSingleWrapper.children[0].children[0].getBoundingClientRect();
                runInAction(() => {
                    this.showExpandIcon = height > (childHeight + 18);
                });
            }
            if (!dataSet.props.autoQuery) {
                this.handleDataSetQuery({ dataSet });
            }
        }
        const shouldInit = dataSet.getState(ORIGINALVALUEOBJ) ? dataSet.getState(ORIGINALVALUEOBJ).query === undefined : true;
        if (shouldInit && queryDataSet && queryDataSet.current) {
            this.initConditionFields({ dataSet: queryDataSet, record: queryDataSet.current });
        }
    }
    componentWillUnmount() {
        const { fuzzyQueryOnly } = this.props;
        if (!fuzzyQueryOnly) {
            document.removeEventListener('click', this.handleClickOut);
            this.processDataSetListener(false);
        }
        if (this.isTooltipShown) {
            hide();
            delete this.isTooltipShown;
        }
    }
    componentWillReceiveProps(nextProps) {
        const { dataSet, fuzzyQueryOnly, queryDataSet } = nextProps;
        // eslint-disable-next-line react/destructuring-assignment
        if (dataSet !== this.props.dataSet || fuzzyQueryOnly !== this.props.fuzzyQueryOnly) {
            runInAction(() => {
                this.fieldSelectHidden = true;
                this.expand = true;
            });
            if (!fuzzyQueryOnly) {
                // 移除原有实例监听
                this.processDataSetListener(false);
                this.processDataSetListener(true, nextProps);
                if (this.isSingleLineOpt() && this.refSingleWrapper) {
                    const { height } = this.refSingleWrapper.getBoundingClientRect();
                    const { height: childHeight } = this.refSingleWrapper.children[0].children[0].getBoundingClientRect();
                    runInAction(() => {
                        this.showExpandIcon = height > (childHeight + 18);
                    });
                }
            }
            const shouldInit = dataSet.getState(ORIGINALVALUEOBJ) ? dataSet.getState(ORIGINALVALUEOBJ).query === undefined : true;
            if (shouldInit && queryDataSet && queryDataSet.current) {
                this.initConditionFields({ dataSet: queryDataSet, record: queryDataSet.current });
            }
        }
    }
    processDataSetListener(flag, nextProps) {
        const { queryDataSet, dataSet } = nextProps || this.props;
        if (queryDataSet) {
            const handler = flag ? queryDataSet.addEventListener : queryDataSet.removeEventListener;
            const dsHandler = flag ? dataSet.addEventListener : dataSet.removeEventListener;
            handler.call(queryDataSet, DataSetEvents.validate, this.handleDataSetValidate);
            handler.call(queryDataSet, DataSetEvents.update, this.handleDataSetUpdate);
            handler.call(queryDataSet, DataSetEvents.create, this.handleDataSetCreate);
            handler.call(queryDataSet, DataSetEvents.reset, this.handleDataSetReset);
            handler.call(queryDataSet, DataSetEvents.load, this.handleDataSetLoad);
            dsHandler.call(dataSet, DataSetEvents.query, this.handleDataSetQuery);
        }
    }
    async handleDataSetQuery({ dataSet }) {
        if (!dataSet.getState(MENURESULT) && this.tableFilterAdapter) {
            await this.initMenuDataSet();
            const res = dataSet.getState(MENURESULT);
            if (res && res.length && res.filter(menu => menu.defaultFlag).length) {
                const defaultMenus = res.filter(menu => menu.defaultFlag);
                const { conditionList } = defaultMenus.length > 1 ? defaultMenus.find(menu => menu.isTenant !== 1) : defaultMenus[0];
                const initQueryData = {};
                if (conditionList && conditionList.length) {
                    map(conditionList, condition => {
                        if (condition.comparator === 'EQUAL') {
                            const { fieldName, value } = condition;
                            initQueryData[fieldName] = parseValue(value);
                        }
                    });
                    const { queryDataSet } = this.props;
                    if (queryDataSet && queryDataSet.current && dataSet.props.autoQuery) {
                        if (Object.keys(initQueryData).length) {
                            dataSet.query();
                            return false;
                        }
                    }
                }
            }
        }
    }
    /**
     * queryDataSet 查询前校验事件 触发展开动态字段
     * @param dataSet 查询DS
     * @param result
     */
    async handleDataSetValidate({ dataSet, result }) {
        if (!await result) {
            runInAction(() => {
                const { current } = dataSet;
                dataSet.fields.forEach((field, key) => {
                    if (!field.isValid(current)) {
                        this.handleSelect(key);
                    }
                });
                this.expand = true;
            });
            const { refSingleWrapper } = this;
            if (refSingleWrapper) {
                refSingleWrapper.style.height = '';
                refSingleWrapper.style.overflow = '';
            }
        }
    }
    setConditionStatus(value, orglValue) {
        const { dataSet } = this.props;
        dataSet.setState(CONDITIONSTATUS, value);
        if (value === RecordStatus.sync && orglValue) {
            const oriObj = dataSet.getState(ORIGINALVALUEOBJ);
            dataSet.setState(ORIGINALVALUEOBJ, { ...oriObj, query: orglValue });
            // this.ORIGINALVALUEOBJ.set('queryFields', orglValue);
        }
    }
    /**
     * 筛选条件更新 触发表格查询
     */
    async handleDataSetUpdate({ record, name, oldValue, value }) {
        const { dataSet, queryDataSet, onQuery = noop, autoQuery } = this.props;
        const field = queryDataSet && queryDataSet.getField(name);
        let shouldQuery = true;
        if (field && field.get('range', record)) {
            // 处理 range 失焦对象 undefined，bind 字段无处理的情况
            if (value) {
                const rangeKeys = Object.keys(value);
                if (rangeKeys.length) {
                    if (value[rangeKeys[0]] === undefined) {
                        record.set(name, null);
                    }
                    else if (value[rangeKeys[1]] === undefined) {
                        record.set(name, {
                            [rangeKeys[0]]: value[rangeKeys[0]],
                            [rangeKeys[1]]: null,
                        });
                    }
                }
            }
            const rangeValue = value ? isArray(value) ? value.join('') : Object.values(value).join('') : '';
            const rangeOldValue = oldValue ? isArray(oldValue) ? oldValue.join('') : Object.values(oldValue).join('') : '';
            shouldQuery = rangeValue !== rangeOldValue;
        }
        let status = RecordStatus.update;
        if (record) {
            status = isEqualDynamicProps(dataSet.getState(ORIGINALVALUEOBJ).query, omit(record.toData(), ['__dirty']), queryDataSet, record, name) ? RecordStatus.sync : RecordStatus.update;
        }
        this.setConditionStatus(status);
        if (autoQuery && shouldQuery) {
            if (await dataSet.modifiedCheck(undefined, dataSet, 'query')) {
                if (queryDataSet && queryDataSet.current && await queryDataSet.current.validate()) {
                    dataSet.query();
                    onQuery();
                }
                else {
                    let hasFocus = false;
                    for (let i = 0; i < this.queryFields.length; i++) {
                        const queryField = this.queryFields[i];
                        const editor = this.refEditors.get(String(queryField.key));
                        if (editor && !editor.valid && !hasFocus && (field && !field.get('multiple', record))) {
                            editor.focus();
                            hasFocus = true;
                        }
                    }
                }
            }
            else {
                record.init(name, oldValue);
            }
        }
    }
    /**
     * queryDS 新建，初始勾选值
     */
    handleDataSetCreate() {
        const { queryDataSet } = this.props;
        if (queryDataSet) {
            this.initConditionFields({ dataSet: queryDataSet, record: queryDataSet.current });
        }
    }
    /**
     * queryDS reset，初始记录
     */
    handleDataSetReset() {
        const { queryDataSet } = this.props;
        if (queryDataSet && !this.tableFilterAdapter) {
            queryDataSet.create();
        }
    }
    /**
     * queryDS load，兼容项目loadData([])的处理，初始化记录
     */
    handleDataSetLoad({ dataSet }) {
        if (!this.tableFilterAdapter && !dataSet.length) {
            dataSet.create();
        }
    }
    /**
     * 初始化勾选值、条件字段
     * @param props
     */
    initConditionFields(props) {
        const { dataSet, record } = props;
        const { dataSet: tableDS } = this.props;
        const originalValue = omit(record.toData(), ['__dirty']);
        const conditionData = Object.entries(originalValue);
        const newObj = tableDS.getState(ORIGINALVALUEOBJ) || {};
        tableDS.setState(ORIGINALVALUEOBJ, { ...newObj, query: originalValue });
        this.originalConditionFields = [];
        const { fields } = dataSet;
        map(conditionData, data => {
            let name = data[0];
            if (!fields.has(data[0]) &&
                isObject(data[1]) &&
                !isEnumEmpty(data[1]) &&
                !isArray(data[1])) {
                name = `${data[0]}.${Object.keys(data[1])[0]}`;
            }
            if (isSelect(data) && !(dataSet.getState(SELECTFIELDS) || []).includes(name)) {
                const field = dataSet.getField(name);
                if (!field || !field.get('bind', record) || field.get('usedFlag')) {
                    this.originalConditionFields.push(name);
                    this.handleSelect(name, record);
                }
            }
        });
    }
    /**
     * 高级搜索字段集
     */
    get advancedSearchFieldProps() {
        const { queryDataSet, dataSet, advancedSearchFields } = this.props;
        if (advancedSearchFields && advancedSearchFields.length) {
            const omitPropsArr = ['dynamicProps', 'computedProps', 'order', 'pattern', 'maxLength', 'minLength', 'max', 'min', 'validator', 'required', 'readOnly', 'disabled', 'defaultValue', 'cascadeMap', 'ignore'];
            return advancedSearchFields.map(field => {
                const { name, source, alias, tableName, fieldPorps } = field;
                if (source === 'fields' && dataSet.props.fields) {
                    const dsFiedlsProps = dataSet.props.fields.find(f => f.name === name);
                    return {
                        ...omit(dsFiedlsProps, omitPropsArr),
                        ...fieldPorps,
                        help: tableName,
                        name: alias || name,
                    };
                }
                if (source === 'queryFields' && queryDataSet && queryDataSet.props.fields) {
                    const dsFiedlsProps = queryDataSet.props.fields.find(f => f.name === name);
                    return {
                        ...omit(dsFiedlsProps, omitPropsArr),
                        ...fieldPorps,
                        help: tableName,
                        name: alias || name,
                    };
                }
                return {
                    ...fieldPorps,
                    // multiple: fieldPorps && fieldPorps.multiple ? '|' : false,
                    help: tableName,
                    name: alias || name,
                };
            });
        }
        return [];
    }
    /**
   * 加载动态筛选条相关初始数据 & 存储初始值
   *  1.筛选条
   *  2.高级搜索
   *  3.模糊搜索
   * @param param
   */
    loadConditionData({ conditionDataSet, newFilterDataSet, menuRecord, dataSet, searchText }) {
        const comparatorData = [];
        const regularData = [];
        const newObj = dataSet.getState(ORIGINALVALUEOBJ) || {};
        let searchTextValue = null;
        menuRecord.get('conditionList').forEach(condition => {
            if (condition.conditionType !== 'comparator') {
                regularData.push(condition);
            }
            else {
                const { fieldName, value, conditionType, comparator, ...rest } = condition;
                comparatorData.push({
                    [AdvancedFieldSet.fieldName]: fieldName,
                    [fieldName]: value,
                    [AdvancedFieldSet.conditionType]: conditionType,
                    [AdvancedFieldSet.comparator]: comparator,
                    ...rest,
                });
            }
        });
        // 加载筛选条数据
        conditionDataSet.loadData(regularData);
        // 加载高级搜索面板数据
        newFilterDataSet.loadData(comparatorData);
        // 初始高级搜索
        dataSet.setState(ORIGINALVALUEOBJ, { ...newObj, advance: comparatorData });
        // 加载模糊搜索数据
        if (menuRecord && menuRecord.get('queryList') && menuRecord.get('queryList').length) {
            const searchObj = menuRecord.get('queryList').find(ql => ql.fieldName === SEARCHTEXT);
            searchTextValue = searchObj ? searchObj.value : null;
            dataSet.setState(SEARCHTEXT, searchTextValue);
            // 初始模糊搜索
            dataSet.setState(ORIGINALVALUEOBJ, { ...newObj, advance: comparatorData, fuzzy: searchTextValue });
            dataSet.setQueryParameter(searchText, searchTextValue);
        }
    }
    /**
     * 初始筛选条数据源状态
     */
    async initMenuDataSet() {
        const { queryDataSet, dataSet, dynamicFilterBar, searchCode } = this.props;
        const { getConfig } = this.context;
        const searchCodes = dynamicFilterBar && dynamicFilterBar.searchCode || searchCode;
        if (this.tableFilterAdapter) {
            const menuDataSet = new DataSet(QuickFilterDataSet({
                searchCode: searchCodes,
                queryDataSet,
                tableFilterAdapter: this.tableFilterAdapter,
            }), { getConfig: getConfig });
            const conditionDataSet = new DataSet(ConditionDataSet(), { getConfig: getConfig });
            const newFilterDataSet = new DataSet(NewFilterDataSet({
                propFields: this.advancedSearchFieldProps,
            }), { getConfig: getConfig });
            const optionDataSet = new DataSet({
                paging: false,
                selection: DataSetSelection.single,
                fields: [
                    {
                        // 是否租户默认配置
                        name: 'isTenant',
                        type: FieldType.string,
                        transformResponse: value => value ? $l('Table', 'preset') : $l('Table', 'user'),
                        group: true,
                    },
                ],
            }, { getConfig: getConfig });
            const filterMenuDataSet = new DataSet({
                autoCreate: true,
                fields: [
                    {
                        name: 'filterName',
                        type: FieldType.string,
                        textField: 'searchName',
                        valueField: 'searchId',
                        options: optionDataSet,
                        ignore: FieldIgnore.always,
                    },
                ],
            }, { getConfig: getConfig });
            let status = RecordStatus.update;
            if (queryDataSet && queryDataSet.current) {
                status = isEqualDynamicProps(dataSet.getState(ORIGINALVALUEOBJ).query, omit(queryDataSet.current.toData(), ['__dirty']), queryDataSet, queryDataSet.current) ? RecordStatus.sync : RecordStatus.update;
            }
            else {
                status = RecordStatus.sync;
            }
            // 初始化状态
            dataSet.setState(MENUDATASET, menuDataSet);
            dataSet.setState(CONDITIONDATASET, conditionDataSet);
            dataSet.setState(NEWFILTERDATASET, newFilterDataSet);
            dataSet.setState(OPTIONDATASET, optionDataSet);
            dataSet.setState(FILTERMENUDATASET, filterMenuDataSet);
            dataSet.setState(CONDITIONSTATUS, status);
            // dataSet.setState(SEARCHTEXT, null);
            const result = await menuDataSet.query();
            dataSet.setState(MENURESULT, result);
            if (optionDataSet) {
                optionDataSet.loadData(result);
            }
            const menuRecord = menuDataSet.current;
            if (menuRecord) {
                this.loadConditionData({ menuRecord, conditionDataSet, newFilterDataSet, dataSet, searchText: this.searchText });
            }
            if (result && result.length) {
                runInAction(() => {
                    this.shouldLocateData = true;
                });
                if (queryDataSet && queryDataSet.fields) {
                    this.tempFields = queryDataSet.snapshot().dataSet.fields;
                }
            }
            else {
                const { current } = filterMenuDataSet;
                if (current)
                    current.set('filterName', undefined);
                runInAction(() => {
                    this.shouldLocateData = true;
                });
            }
        }
        return true;
    }
    /**
     * tableFilterSuffix 预留自定义区域
     */
    renderSuffix() {
        const { dynamicFilterBar, queryDataSet, dataSet, buttons = [] } = this.props;
        const { getConfig } = this.context;
        const { prefixCls } = this;
        const suffixes = dynamicFilterBar && dynamicFilterBar.suffixes || getConfig('tableFilterSuffix');
        const children = [];
        let suffixesDom = null;
        const tableButtons = buttons.length ? (React.createElement(TableButtons, { key: "toolbar", prefixCls: `${prefixCls}-dynamic-filter`, buttons: buttons })) : null;
        if (suffixes && suffixes.length) {
            suffixes.forEach(suffix => {
                if (suffix === 'filter') {
                    children.push(React.createElement(ColumnFilter, { prefixCls: prefixCls }));
                }
                else if (isValidElement(suffix)) {
                    children.push(suffix);
                }
                else if (isFunction(suffix)) {
                    children.push(suffix({ queryDataSet, dataSet }));
                }
            });
            suffixesDom = (React.createElement("div", { className: `${prefixCls}-dynamic-filter-bar-suffix` }, children));
        }
        return [
            tableButtons,
            suffixesDom,
        ];
    }
    getPrefix() {
        const { dynamicFilterBar, queryDataSet, dataSet } = this.props;
        const { prefixCls } = this;
        const prefixes = dynamicFilterBar && dynamicFilterBar.prefixes;
        const children = [];
        if (prefixes && prefixes.length) {
            prefixes.forEach((prefix) => {
                if (isString(prefix) && prefix === 'filter') {
                    children.push(React.createElement(ColumnFilter, { prefixCls: prefixCls }));
                }
                else if (isValidElement(prefix)) {
                    children.push(prefix);
                }
                else if (isFunction(prefix)) {
                    children.push(prefix({ queryDataSet, dataSet }));
                }
            });
            return (React.createElement(React.Fragment, null,
                React.createElement("div", { className: `${prefixCls}-dynamic-filter-bar-prefix` }, children),
                React.createElement("span", { className: `${prefixCls}-filter-search-divide` })));
        }
        return null;
    }
    /**
     * 注入 refEditors
     * @param element
     * @param name
     */
    createFields(element, name) {
        const props = {
            ref: (node) => this.refEditors.set(name, node),
            border: false,
            clearButton: true,
            _inTable: true,
            showValidation: 'tooltip',
        };
        return cloneElement(element, props);
    }
    /**
     * 判断查询值是否为空
     * @param value
     */
    isEmpty(value) {
        return isArrayLike(value) ? !value.length : isEmpty(value);
    }
    get tableFilterAdapter() {
        const { dynamicFilterBar, searchCode } = this.props;
        const { getConfig } = this.context;
        const searchCodes = dynamicFilterBar && dynamicFilterBar.searchCode || searchCode;
        return searchCodes ? dynamicFilterBar && dynamicFilterBar.tableFilterAdapter || getConfig('tableFilterAdapter') : null;
    }
    /**
     * 是否单行操作
     */
    isSingleLineOpt() {
        const { fuzzyQuery } = this.props;
        return !(fuzzyQuery || this.tableFilterAdapter);
    }
    /**
     * 查询前修改提示及校验定位
     */
    async modifiedCheckQuery(fuzzyValue, fuzzyOldValue, refresh) {
        const { dataSet, queryDataSet, fuzzyQueryOnly, onRefresh = noop } = this.props;
        if (await dataSet.modifiedCheck(undefined, dataSet, 'query')) {
            if ((queryDataSet && queryDataSet.current && await queryDataSet.current.validate()) || fuzzyQueryOnly) {
                if (fuzzyValue) {
                    runInAction(() => {
                        dataSet.setState(SEARCHTEXT, fuzzyValue || null);
                    });
                    dataSet.setQueryParameter(this.searchText, fuzzyValue);
                }
                if (refresh) {
                    if ((await onRefresh()) !== false) {
                        dataSet.query();
                    }
                }
                else {
                    dataSet.query();
                }
            }
            else {
                let hasFocus = false;
                for (const [key, value] of this.refEditors.entries()) {
                    if (value && !value.valid && !hasFocus) {
                        this.refEditors.get(key).focus();
                        hasFocus = true;
                    }
                }
            }
        }
        else {
            runInAction(() => {
                dataSet.setState(SEARCHTEXT, fuzzyOldValue || null);
            });
            dataSet.setQueryParameter(this.searchText, fuzzyOldValue);
        }
    }
    renderRefreshBtn() {
        const { prefixCls } = this;
        return (React.createElement("span", { className: `${prefixCls}-filter-menu-query`, onClick: async (e) => {
                e.stopPropagation();
                await this.modifiedCheckQuery(undefined, undefined, true);
            } },
            React.createElement(Tooltip, { title: $l('Table', 'refresh') },
                React.createElement(Icon, { type: "refresh" }))));
    }
    /**
     * 渲染展开逻辑
     * @param hidden 是否隐藏全部
     */
    getExpandNode(hidden) {
        const { prefixCls, props: { refreshBtn } } = this;
        if (!this.showExpandIcon && !hidden)
            return refreshBtn ? this.renderRefreshBtn() : null;
        return (React.createElement("span", { className: `${prefixCls}-filter-menu-expand`, onClick: () => {
                const { refSingleWrapper } = this;
                if (refSingleWrapper) {
                    const { height } = refSingleWrapper.getBoundingClientRect();
                    const { height: childHeight } = refSingleWrapper.children[0].children[0].getBoundingClientRect();
                    runInAction(() => {
                        this.expand = hidden ? height <= 0 : height <= (childHeight + 18);
                    });
                    if (hidden && height) {
                        // 收起全部
                        refSingleWrapper.style.display = 'none';
                    }
                    else {
                        refSingleWrapper.style.display = 'flex';
                        refSingleWrapper.style.height = '';
                        refSingleWrapper.style.overflow = '';
                    }
                    if (height > (childHeight + 18) && !hidden) {
                        // 收起留一行高度
                        refSingleWrapper.style.height = pxToRem(childHeight + 18, true) || '';
                        refSingleWrapper.style.overflow = 'hidden';
                    }
                    else {
                        refSingleWrapper.style.height = '';
                        refSingleWrapper.style.overflow = '';
                    }
                }
            } },
            refreshBtn ? this.renderRefreshBtn() : null,
            this.expand ? (React.createElement(Tooltip, { title: $l('Table', 'collapse') },
                React.createElement(Icon, { type: "baseline-arrow_drop_up" }))) : (React.createElement(Tooltip, { title: $l('Table', 'expand_button') },
                React.createElement(Icon, { type: "baseline-arrow_drop_down" })))));
    }
    /**
     * 过滤比较符
     * @param record
     * @param optionRecord
     * @returns
     */
    optionsFilter(record, optionRecord) {
        if (!record.get(AdvancedFieldSet.fieldName))
            return false;
        const { dataSet } = this.props;
        const newFilterDataSet = dataSet.getState(NEWFILTERDATASET);
        const field = newFilterDataSet.getField(record.get(AdvancedFieldSet.fieldName), record);
        const type = field.get('type', record);
        const value = OPERATOR[optionRecord.get('value').toUpperCase()];
        if (!OPERATOR_TYPE[type.toUpperCase()]) {
            return OPERATOR_TYPE.STRING.includes(value);
        }
        if (field.get('lookupCode', record) ||
            isString(field.get('lookupUrl', record)) ||
            (type !== FieldType.object && (field.get('lovCode', record) || field.getLookup(record) || field.get('options', record)))) {
            return OPERATOR_TYPE.LOOKUP.includes(value);
        }
        if (field.get('lovCode', record)) {
            return OPERATOR_TYPE.LOV.includes(value);
        }
        return OPERATOR_TYPE[type.toUpperCase()].includes(value);
    }
    /**
     * 高级筛选弹窗确认
     */
    async handleFilterOk() {
        const { dataSet, autoQuery } = this.props;
        const newFilterDataSet = dataSet.getState(NEWFILTERDATASET);
        const res = await newFilterDataSet.validate();
        if (res) {
            runInAction(async () => {
                // 初始状态下移除空行
                if (newFilterDataSet.length !== 1) {
                    newFilterDataSet.map(r => {
                        if (!r.get(AdvancedFieldSet.fieldName)) {
                            newFilterDataSet.remove(r, true);
                        }
                        return null;
                    });
                }
                const jsonData = newFilterDataSet.toJSONData();
                processFilterParam(dataSet);
                const status = isEqualDynamicProps(toJS(dataSet.getState(ORIGINALVALUEOBJ).advance), jsonData, newFilterDataSet) ? dataSet.getState(CONDITIONSTATUS) : RecordStatus.update;
                // 更新筛选条状态
                this.setConditionStatus(status);
                if (autoQuery)
                    dataSet.query();
                this.visible = false;
            });
        }
    }
    /**
     * 渲染高级筛选面板
     */
    getAdvancedFilter() {
        const { prefixCls, context: { getConfig } } = this;
        const { dataSet, advancedSearchFields } = this.props;
        const tlsKey = getConfig('tlsKey');
        const newFilterDataSet = dataSet.getState(NEWFILTERDATASET);
        const menuDataSet = dataSet.getState(MENUDATASET);
        if (advancedSearchFields && advancedSearchFields.length && newFilterDataSet) {
            const selectOptions = this.advancedSearchFieldProps.map(fieldProps => {
                const { name, label, bind } = fieldProps;
                if (bind)
                    return null;
                const field = newFilterDataSet.getField(name);
                const hasBindProps = (propsName) => field && field.get(propsName) && field.get(propsName).bind;
                if (field.get('bind') ||
                    hasBindProps('computedProps') ||
                    hasBindProps('dynamicProps') ||
                    field.get('name').includes(tlsKey)) {
                    return null;
                }
                return React.createElement(Select.Option, { key: `${name}_select_option`, value: name }, label || name);
            });
            const PopoverContent = (React.createElement(React.Fragment, null,
                React.createElement(Tooltip, { theme: "light", title: dataSet.getState(EXPTYPE) === 'customize' ? $l('Table', 'ad_search_help') : null },
                    React.createElement(Select, { isFlat: true, border: true, 
                        // dataSet={menuDataSet.current}
                        // name="conExpression"
                        value: dataSet.getState(EXPTYPE) ? dataSet.getState(EXPTYPE) : (['all', 'either'].includes(menuDataSet.current && menuDataSet.current.get('conExpression')) ? menuDataSet.current.get('conExpression') : "all"), onChange: (value) => {
                            dataSet.setState(EXPTYPE, value);
                            if (value !== 'customize') {
                                dataSet.setState(SEARCHEXP, undefined);
                            }
                            if (menuDataSet.current) {
                                menuDataSet.current.set('conExpression', value);
                            }
                        }, clearButton: false, className: `${prefixCls}-new-filter-popover-select` },
                        React.createElement(Select.Option, { value: "all" }, $l('Table', 'ad_search_all')),
                        React.createElement(Select.Option, { value: "either" }, $l('Table', 'ad_search_any')),
                        React.createElement(Select.Option, { value: "customize" },
                            $l('Table', 'ad_search_custom'),
                            React.createElement(Tooltip, { theme: "light", title: $l('Table', 'ad_search_help') },
                                React.createElement(Icon, { className: `${prefixCls}-new-filter-popover-select-icon`, type: "help_outline" }))))),
                dataSet.getState(EXPTYPE) === 'customize' ?
                    React.createElement(TextField, { clearButton: true, required: true, value: dataSet.getState(SEARCHEXP) ?
                            dataSet.getState(SEARCHEXP) :
                            (menuDataSet.current && menuDataSet.current.get('conExpression') !== 'customize' ? menuDataSet.current.get('conExpression') : undefined), onChange: (value) => {
                            dataSet.setState(SEARCHEXP, value);
                            if (menuDataSet.current) {
                                menuDataSet.current.set('conExpression', value);
                            }
                        }, className: `${prefixCls}-new-filter-popover-input`, placeholder: $l('Table', 'ad_search_placeholder') })
                    : null,
                React.createElement("div", { className: `${prefixCls}-new-filter-popover-line` }),
                React.createElement(Tree, { key: "__filter", dataSet: newFilterDataSet, showLine: {
                        showLeafIcon: false,
                    }, className: `${prefixCls}-new-filter-popover-tree`, onTreeNode: () => ({
                        className: `${prefixCls}-new-filter-popover-tree-node`,
                    }), showIcon: true, icon: (props) => {
                        return React.createElement(Tag, { className: `${prefixCls}-new-filter-popover-item-tag` }, Number(props.pos.split('0-')[1]) + 1);
                    }, renderer: ({ record }) => {
                        if (!record)
                            return null;
                        const comparator = record.get(AdvancedFieldSet.comparator);
                        const fieldName = record.get(AdvancedFieldSet.fieldName);
                        const field = record.getField(fieldName);
                        let valueFieldDom = React.createElement(TextField, { style: { width: 180 }, disabled: true });
                        if (field && !field.get('bind', record) && !fieldName.includes(tlsKey)) {
                            valueFieldDom = cloneElement(getEditorByField(field, record, true), {
                                help: '',
                                key: fieldName,
                                name: fieldName,
                                record,
                                style: { width: 180 },
                                disabled: [OPERATOR.IS_NULL.value, OPERATOR.IS_NOT_NULL.value].includes(comparator),
                            });
                        }
                        return (React.createElement("div", { className: `${prefixCls}-new-filter-popover-item`, key: `${record.index}_option` },
                            React.createElement(Select, { name: AdvancedFieldSet.fieldName, style: { width: 120 }, record: record, placeholder: $l('Lov', 'choose'), optionTooltip: OptionTooltip.overflow }, selectOptions),
                            React.createElement(Select, { name: AdvancedFieldSet.comparator, renderer: ({ value, text }) => value ? $l('Operator', value.toLowerCase()) : text, optionRenderer: ({ value, text }) => value ? $l('Operator', value.toLowerCase()) : text, style: { width: 88 }, dropdownMatchSelectWidth: false, optionTooltip: OptionTooltip.overflow, record: record, disabled: !record.get(AdvancedFieldSet.fieldName), optionsFilter: (optionRecord) => this.optionsFilter(record, optionRecord) }),
                            valueFieldDom,
                            React.createElement(Button, { funcType: FuncType.link, icon: "delete_black-o", color: ButtonColor.primary, onClick: () => {
                                    if (newFilterDataSet.length === 1) {
                                        newFilterDataSet.removeAll();
                                    }
                                    else {
                                        newFilterDataSet.remove(record);
                                    }
                                } })));
                    } }),
                React.createElement(Button, { funcType: FuncType.link, icon: "add", color: ButtonColor.primary, onClick: () => {
                        newFilterDataSet.create();
                    } }, $l('Table', 'ad_search_add')),
                React.createElement("div", { className: `${prefixCls}-new-filter-popover-footer` },
                    React.createElement(Button, { onClick: () => {
                            const orgValue = dataSet.getState(ORIGINALVALUEOBJ).advance;
                            newFilterDataSet.loadData(orgValue);
                            this.setConditionStatus(dataSet.getState(CONDITIONSTATUS));
                            runInAction(() => {
                                this.visible = false;
                            });
                        } }, $l('Modal', 'cancel')),
                    React.createElement(Button, { onClick: this.handleFilterOk, color: ButtonColor.primary }, $l('Modal', 'ok')))));
            return (React.createElement(Popover, { title: $l('Table', 'ad_search_title'), content: PopoverContent, overlayClassName: `${prefixCls}-new-filter-popover`, trigger: "click", visible: this.visible },
                React.createElement(Button, { funcType: FuncType.link, className: `${prefixCls}-new-filter-popover-icon`, icon: "filter2", color: newFilterDataSet && newFilterDataSet.some(r => r.status === 'sync') ? ButtonColor.primary : ButtonColor.default, onClick: () => {
                        if (!newFilterDataSet.length) {
                            newFilterDataSet.create();
                        }
                        runInAction(() => {
                            this.visible = true;
                        });
                    } })));
        }
        return null;
    }
    /**
     * 渲染模糊搜索
     */
    getFuzzyQuery() {
        const { dataSet, fuzzyQuery, fuzzyQueryPlaceholder, onReset = noop } = this.props;
        const { prefixCls } = this;
        const placeholder = fuzzyQueryPlaceholder || $l('Table', 'enter_search_content');
        const fuzzyValue = dataSet.getState(ORIGINALVALUEOBJ) && dataSet.getState(ORIGINALVALUEOBJ).fuzzy;
        if (!fuzzyQuery)
            return null;
        return (React.createElement("div", { className: `${prefixCls}-filter-search` },
            React.createElement(TextField, { style: { width: 280 }, clearButton: true, placeholder: placeholder, prefix: React.createElement(Icon, { type: "search" }), value: dataSet.getState(SEARCHTEXT), valueChangeAction: ValueChangeAction.input, waitType: WaitType.debounce, wait: 500, onChange: (value, oldValue) => {
                    this.handleQuery(undefined, value, oldValue);
                    dataSet.setState(SEARCHTEXT, value);
                    dataSet.setQueryParameter(this.searchText, value);
                    this.setConditionStatus(value === fuzzyValue ? RecordStatus.sync : RecordStatus.update);
                }, onClear: () => {
                    runInAction(() => {
                        dataSet.setState(SEARCHTEXT, null);
                    });
                    onReset();
                } })));
    }
    /**
     * 渲染重置按钮
     */
    getResetButton() {
        const { queryDataSet, dataSet, autoQueryAfterReset, onReset = noop } = this.props;
        const { prefixCls } = this;
        return (React.createElement("div", { className: `${prefixCls}-filter-buttons` }, dataSet.getState(CONDITIONSTATUS) === RecordStatus.update && (React.createElement(Button, { onClick: () => {
                let shouldQuery = false;
                if (queryDataSet) {
                    const { current } = queryDataSet;
                    if (current) {
                        const orgObj = dataSet.getState(ORIGINALVALUEOBJ);
                        shouldQuery = (dataSet.getState(SEARCHTEXT) !== orgObj.fuzzy) || !isEqualDynamicProps(orgObj.query, omit(current.toData(), ['__dirty']), queryDataSet, current);
                        dataSet.setState(SEARCHTEXT, orgObj.fuzzy);
                        dataSet.setQueryParameter(this.searchText, orgObj.fuzzy);
                        current.reset();
                        dataSet.setState(SELECTFIELDS, [...this.originalConditionFields]);
                    }
                }
                this.setConditionStatus(RecordStatus.sync);
                onReset();
                if (autoQueryAfterReset && shouldQuery) {
                    this.handleQuery();
                }
            } }, $l('Table', 'reset_button')))));
    }
    /**
     * 筛选头
     * fuzzyQuery + quickFilterMenu + resetButton + buttons
     */
    getFilterMenu() {
        const { defaultActiveKey, queryFields, queryDataSet, dataSet, dynamicFilterBar, searchCode, autoQuery, fuzzyQueryOnly, sortableFieldNames } = this.props;
        const { prefixCls } = this;
        const prefix = this.getPrefix();
        const suffix = this.renderSuffix();
        const fuzzyQuery = this.getFuzzyQuery();
        const advancedFilter = this.getAdvancedFilter();
        if (fuzzyQueryOnly) {
            return (React.createElement("div", { className: `${prefixCls}-filter-menu` },
                prefix,
                advancedFilter,
                fuzzyQuery,
                suffix));
        }
        if (queryDataSet && queryFields.length) {
            const searchCodes = dynamicFilterBar && dynamicFilterBar.searchCode || searchCode;
            const quickFilterMenu = this.tableFilterAdapter && searchCodes ? (React.createElement(QuickFilterMenuContext.Provider, { value: {
                    tempQueryFields: this.tempFields,
                    autoQuery,
                    prefixCls,
                    expand: this.expand,
                    dataSet,
                    queryDataSet,
                    refEditors: this.refEditors,
                    onChange: this.handleSelect,
                    conditionStatus: dataSet.getState(CONDITIONSTATUS),
                    onStatusChange: this.setConditionStatus,
                    selectFields: dataSet.getState(SELECTFIELDS),
                    onOriginalChange: this.setOriginalConditionFields,
                    menuDataSet: dataSet.getState(MENUDATASET),
                    filterMenuDataSet: dataSet.getState(FILTERMENUDATASET),
                    conditionDataSet: dataSet.getState(CONDITIONDATASET),
                    newFilterDataSet: dataSet.getState(NEWFILTERDATASET),
                    optionDataSet: dataSet.getState(OPTIONDATASET),
                    shouldLocateData: this.shouldLocateData,
                    initConditionFields: this.initConditionFields,
                    sortableFieldNames,
                    searchText: this.searchText,
                    loadConditionData: this.loadConditionData,
                    defaultActiveKey,
                } },
                React.createElement(QuickFilterMenu, null))) : null;
            const resetButton = this.isSingleLineOpt() || this.tableFilterAdapter ? null : this.getResetButton();
            return (React.createElement("div", { className: `${prefixCls}-filter-menu` },
                prefix,
                fuzzyQuery,
                advancedFilter,
                quickFilterMenu,
                resetButton,
                this.isSingleLineOpt() ? null : (React.createElement(React.Fragment, null,
                    React.createElement("span", { className: `${prefixCls}-filter-search-divide` }),
                    this.getExpandNode(true))),
                suffix));
        }
    }
    /**
     * 查询字段初始顺序
     * 排除动态属性影响
     */
    get originOrderQueryFields() {
        const { queryDataSet } = this.props;
        const { getConfig } = this.context;
        const result = [];
        const tlsKey = getConfig('tlsKey');
        if (queryDataSet) {
            const { fields, props: { fields: propFields = [] } } = queryDataSet;
            const cloneFields = fields.toJS();
            propFields.forEach(({ name }) => {
                if (name) {
                    const field = cloneFields.get(name);
                    const hasBindProps = (propsName) => field && field.get(propsName) && field.get(propsName).bind;
                    if (field &&
                        !field.get('bind') &&
                        !hasBindProps('computedProps') &&
                        !hasBindProps('dynamicProps') &&
                        !field.get('name').includes(tlsKey)) {
                        result.push(field);
                    }
                }
            });
        }
        return result;
    }
    /**
     * 获取模糊搜索字段参数名
     */
    get searchText() {
        const { dynamicFilterBar } = this.props;
        const { getConfig } = this.context;
        return dynamicFilterBar && dynamicFilterBar.searchText || getConfig('tableFilterSearchText') || 'params';
    }
    handleHelpMouseEnter(e, help) {
        const { target } = e;
        const { getTooltipTheme, getTooltipPlacement } = this.context;
        show(target, {
            title: help,
            theme: getTooltipTheme('help'),
            placement: getTooltipPlacement('help'),
        });
        this.isTooltipShown = true;
    }
    handleHelpMouseLeave() {
        hide();
    }
    renderTooltipHelp(help) {
        if (help) {
            return (React.createElement(Icon, { type: "help", onMouseEnter: (e) => this.handleHelpMouseEnter(e, help), onMouseLeave: this.handleHelpMouseLeave }));
        }
    }
    /**
     * 渲染查询项 label
     * @param param
     * @returns
     */
    getLabel({ field, value, record, placeholder }) {
        if (value) {
            return field.get('label', record);
        }
        return field.get('label', record) || placeholder;
    }
    /**
     * 渲染查询条
     */
    getQueryBar() {
        const { getConfig } = this.context;
        const { queryFieldsLimit = 3, queryFields, queryDataSet, dataSet, fuzzyQueryOnly } = this.props;
        const menuDataSet = dataSet.getState(MENUDATASET);
        const isTenant = menuDataSet && menuDataSet.current && menuDataSet.current.get('isTenant');
        let fieldsLimit = queryFieldsLimit;
        if (isTenant) {
            fieldsLimit = 0;
        }
        const { prefixCls } = this;
        const selectFields = dataSet.getState(SELECTFIELDS) || [];
        if (fuzzyQueryOnly) {
            return (React.createElement("div", { key: "query_bar", className: `${prefixCls}-dynamic-filter-bar` }, this.getFilterMenu()));
        }
        if (queryDataSet && queryFields.length) {
            const singleLineModeAction = this.isSingleLineOpt() ?
                React.createElement("div", { className: `${prefixCls}-dynamic-filter-bar-single-action` },
                    this.getResetButton(),
                    this.getExpandNode(false)) : null;
            return (React.createElement("div", { key: "query_bar", className: `${prefixCls}-dynamic-filter-bar` },
                this.getFilterMenu(),
                React.createElement("div", { className: `${prefixCls}-dynamic-filter-single-wrapper`, ref: (node) => this.refSingleWrapper = node },
                    React.createElement("div", { className: `${prefixCls}-filter-wrapper` },
                        this.queryFields.slice(0, fieldsLimit).map(element => {
                            const { name, hidden, showHelp, disabled, help, placeholder } = element.props;
                            const isLabelShowHelp = (showHelp || getConfig('showHelp')) === ShowHelp.label;
                            if (hidden)
                                return null;
                            const queryField = queryDataSet.getField(name);
                            const isRequired = queryField && queryField.get('required');
                            const validationMessage = queryField && queryField.getValidationMessage(queryDataSet.current);
                            const hasValue = !this.isEmpty(queryDataSet.current && queryDataSet.current.get(name));
                            const label = this.getLabel({ field: queryField, value: hasValue, placeholder, record: queryDataSet.current });
                            const isDisabled = disabled || (queryField && queryField.get('disabled', queryDataSet.current));
                            const itemContentClassName = classNames(`${prefixCls}-filter-content`, {
                                [`${prefixCls}-filter-content-disabled`]: isDisabled,
                                [`${prefixCls}-filter-content-required`]: isRequired,
                                [`${prefixCls}-filter-content-has-value`]: hasValue,
                                [`${prefixCls}-filter-content-invalid`]: validationMessage,
                            });
                            return (React.createElement("div", { className: itemContentClassName, key: name, onMouseDown: () => {
                                    if (!isDisabled) {
                                        const editor = this.refEditors.get(name);
                                        if (editor) {
                                            defer(() => {
                                                this.refEditors.get(name).focus();
                                            }, 50);
                                        }
                                    }
                                }, onClick: () => {
                                    if (!isDisabled) {
                                        const filterItem = this.refFilterItems.get(name);
                                        if (filterItem) {
                                            if (!filterItem.className.includes("c7n-pro-lov-click")) {
                                                filterItem.className += ' c7n-pro-lov-click';
                                            }
                                        }
                                    }
                                }, onBlur: () => {
                                    const filterItem = this.refFilterItems.get(name);
                                    if (filterItem && filterItem.className.includes("c7n-pro-lov-click")) {
                                        filterItem.className = filterItem.className.split(" c7n-pro-lov-click")[0];
                                    }
                                }, onMouseEnter: (e) => {
                                    if (validationMessage) {
                                        const { currentTarget } = e;
                                        show(currentTarget, {
                                            title: utilRenderValidationMessage(validationMessage, true),
                                            theme: 'light',
                                            placement: 'top',
                                        });
                                        this.isTooltipShown = true;
                                    }
                                }, onMouseLeave: () => {
                                    hide();
                                } },
                                React.createElement("span", { className: `${prefixCls}-filter-label` },
                                    label,
                                    isLabelShowHelp ? this.renderTooltipHelp(help || queryField && queryField.get('help', queryDataSet.current)) : null),
                                React.createElement("span", { className: classNames(`${prefixCls}-filter-item`, {
                                        [`${prefixCls}-filter-item-has-value`]: hasValue,
                                    }), ref: (node) => this.refFilterItems.set(name, node) }, this.createFields(element, name))));
                        }),
                        this.queryFields.slice(fieldsLimit).map(element => {
                            const { name, hidden, showHelp, disabled, help, placeholder } = element.props;
                            const isLabelShowHelp = (showHelp || getConfig('showHelp')) === ShowHelp.label;
                            if (hidden)
                                return null;
                            const queryField = queryDataSet.getField(name);
                            const isRequired = queryField && queryField.get('required');
                            const validationMessage = queryField && queryField.getValidationMessage(queryDataSet.current);
                            const hasValue = !this.isEmpty(queryDataSet.current && queryDataSet.current.get(name));
                            const label = this.getLabel({ field: queryField, value: hasValue, placeholder, record: queryDataSet.current });
                            const isDisabled = disabled || (queryField && queryField.get('disabled', queryDataSet.current));
                            const itemContentClassName = classNames(`${prefixCls}-filter-content`, {
                                [`${prefixCls}-filter-content-disabled`]: isDisabled,
                                [`${prefixCls}-filter-content-required`]: isRequired,
                                [`${prefixCls}-filter-content-has-value`]: hasValue,
                                [`${prefixCls}-filter-content-invalid`]: validationMessage,
                            });
                            if (selectFields.includes(name)) {
                                return (React.createElement("div", { className: itemContentClassName, key: name, onMouseDown: () => {
                                        if (!isDisabled) {
                                            const editor = this.refEditors.get(name);
                                            if (editor) {
                                                defer(() => {
                                                    this.refEditors.get(name).focus();
                                                }, 50);
                                            }
                                        }
                                    }, onClick: () => {
                                        if (!isDisabled) {
                                            const filterItem = this.refFilterItems.get(name);
                                            if (filterItem) {
                                                if (!filterItem.className.includes("c7n-pro-lov-click")) {
                                                    filterItem.className += ' c7n-pro-lov-click';
                                                }
                                            }
                                        }
                                    }, onBlur: () => {
                                        const filterItem = this.refFilterItems.get(name);
                                        if (filterItem && filterItem.className.includes("c7n-pro-lov-click")) {
                                            filterItem.className = filterItem.className.split(" c7n-pro-lov-click")[0];
                                        }
                                    }, onMouseEnter: (e) => {
                                        if (validationMessage) {
                                            const { currentTarget } = e;
                                            show(currentTarget, {
                                                title: utilRenderValidationMessage(validationMessage, true),
                                                theme: 'light',
                                                placement: 'top',
                                            });
                                            this.isTooltipShown = true;
                                        }
                                    }, onMouseLeave: () => {
                                        hide();
                                    } },
                                    React.createElement(Icon, { type: "cancel", className: `${prefixCls}-filter-item-close`, onMouseDown: (e) => {
                                            e.stopPropagation();
                                        }, onClick: () => {
                                            this.handleUnSelect([name]);
                                        } }),
                                    React.createElement("span", { className: `${prefixCls}-filter-label` },
                                        label,
                                        isLabelShowHelp ? this.renderTooltipHelp(help || queryField && queryField.get('help', queryDataSet.current)) : null),
                                    React.createElement("span", { className: classNames(`${prefixCls}-filter-item`, {
                                            [`${prefixCls}-filter-item-has-value`]: hasValue,
                                        }), ref: (node) => this.refFilterItems.set(name, node) }, this.createFields(element, name))));
                            }
                            return null;
                        }),
                        (fieldsLimit < this.queryFields.length) && (React.createElement("div", { className: `${prefixCls}-filter-item` },
                            React.createElement(Dropdown, { visible: !this.fieldSelectHidden, overlay: (React.createElement("div", { role: "none", ref: (node) => this.refDropdown = node, onClick: (e) => {
                                        e.stopPropagation();
                                    } },
                                    React.createElement(FieldList, { groups: [{
                                                title: $l('Table', 'predefined_fields'),
                                                fields: isTenant ? [...queryDataSet.fields.values()] : this.originOrderQueryFields.slice(fieldsLimit),
                                            }], prefixCls: `${prefixCls}-filter-list` || 'c7n-pro-table-filter-list', closeMenu: () => runInAction(() => this.fieldSelectHidden = true), value: selectFields, onSelect: this.handleSelect, onUnSelect: this.handleUnSelect }))), trigger: [Action.click] },
                                React.createElement("span", { className: `${prefixCls}-add-fields`, onClick: (e) => {
                                        e.nativeEvent.stopImmediatePropagation();
                                        runInAction(() => {
                                            this.fieldSelectHidden = false;
                                        });
                                    } },
                                    React.createElement(Icon, { type: "add" }),
                                    $l('Table', 'add_filter')))))),
                    singleLineModeAction)));
        }
        return null;
    }
    /**
     *
     * @param collapse
     * @param value 模糊查询参数
     */
    async handleQuery(collapse, value, oldValue) {
        const { onQuery = noop, autoQuery } = this.props;
        if (autoQuery) {
            await this.modifiedCheckQuery(value, oldValue);
        }
        if (!collapse) {
            onQuery();
        }
    }
    render() {
        const { summaryBar, buttons } = this.props;
        const { prefixCls } = this;
        const queryBar = this.getQueryBar();
        if (queryBar) {
            return [queryBar, summaryBar];
        }
        return React.createElement(TableButtons, { key: "toolbar", prefixCls: `${prefixCls}-dynamic-filter-buttons`, buttons: buttons }, summaryBar);
    }
};
TableDynamicFilterBar.defaultProps = {
    queryFieldsLimit: 3,
    autoQueryAfterReset: true,
    fuzzyQuery: true,
    fuzzyQueryOnly: false,
    autoQuery: true,
    refreshBtn: true,
    buttons: [],
};
__decorate([
    observable
], TableDynamicFilterBar.prototype, "moreFields", void 0);
__decorate([
    observable
], TableDynamicFilterBar.prototype, "fieldSelectHidden", void 0);
__decorate([
    observable
], TableDynamicFilterBar.prototype, "expand", void 0);
__decorate([
    observable
], TableDynamicFilterBar.prototype, "visible", void 0);
__decorate([
    observable
], TableDynamicFilterBar.prototype, "shouldLocateData", void 0);
__decorate([
    observable
], TableDynamicFilterBar.prototype, "showExpandIcon", void 0);
__decorate([
    action
], TableDynamicFilterBar.prototype, "handleClickOut", void 0);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleDataSetQuery", null);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleDataSetValidate", null);
__decorate([
    autobind,
    action
], TableDynamicFilterBar.prototype, "setConditionStatus", null);
__decorate([
    action
], TableDynamicFilterBar.prototype, "setOriginalConditionFields", void 0);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleDataSetUpdate", null);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleDataSetCreate", null);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleDataSetReset", null);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleDataSetLoad", null);
__decorate([
    autobind,
    action
], TableDynamicFilterBar.prototype, "initConditionFields", null);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "initMenuDataSet", null);
__decorate([
    action
], TableDynamicFilterBar.prototype, "handleSelect", void 0);
__decorate([
    action
], TableDynamicFilterBar.prototype, "handleUnSelect", void 0);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleFilterOk", null);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleHelpMouseEnter", null);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleHelpMouseLeave", null);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleQuery", null);
TableDynamicFilterBar = __decorate([
    observer
], TableDynamicFilterBar);
export default TableDynamicFilterBar;
